import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, AlertCircle, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { format, parseISO } from "date-fns";

const TEAL = "#2EB4AC";
const TEAL_DARK = "#259993";

const compactNumber = (v) => {
  if (v == null || isNaN(v)) return "0";
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}m`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}k`.replace(".0k", "k");
  return n.toLocaleString("en-IN");
};

const formatINR = (v) => {
  const n = Number(v) || 0;
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const lastMondayMidnight = () => {
  const now = new Date();
  const daysSinceMon = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
  const d = new Date(now);
  d.setDate(now.getDate() - daysSinceMon);
  d.setHours(0, 0, 0, 0);
  return d;
};

const authHeaders = () => ({ Authorization: `Bearer ${getAuthToken()}` });

export default function CostTrackerWidget() {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creditsInput, setCreditsInput] = useState("");
  const [rateInput, setRateInput] = useState("");
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);

  // Initial load
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [stateRes, histRes] = await Promise.all([
        axios.get(`${API}/cost-tracker`, { headers: authHeaders() }),
        axios.get(`${API}/cost-tracker/history?weeks=12`, { headers: authHeaders() }),
      ]);
      setState(stateRes.data);
      setHistory(histRes.data?.history || []);
    } catch {
      // silent — widget is discreet; don't toast on fetch failure
    }
  };

  // Click-outside collapses the expanded pill (but ignore clicks while dialog is open).
  useEffect(() => {
    if (!expanded) return undefined;
    const handler = (e) => {
      if (dialogOpen) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [expanded, dialogOpen]);

  const totalCredits = Number(state?.total_credits) || 0;
  const ratePerCredit = Number(state?.cost_per_credit_inr) || 13.70;
  const totalSpent = totalCredits * ratePerCredit;

  const stale = useMemo(() => {
    if (!state?.updated_at) return true;
    try {
      return new Date(state.updated_at) < lastMondayMidnight();
    } catch {
      return true;
    }
  }, [state?.updated_at]);

  // Sparkline data (memoized)
  const sparklinePoints = useMemo(() => {
    if (!history || history.length < 2) return null;
    const w = 56, h = 18, pad = 2;
    const credits = history.map((p) => p.credits || 0);
    const max = Math.max(...credits);
    const min = Math.min(...credits);
    const range = max - min || 1;
    const stepX = (w - pad * 2) / (credits.length - 1);
    const points = credits.map((c, i) => {
      const x = pad + i * stepX;
      const y = h - pad - ((c - min) / range) * (h - pad * 2);
      return [x, y];
    });
    return { points, w, h };
  }, [history]);

  // Trend delta vs prior week
  const trend = useMemo(() => {
    if (!history || history.length < 2) return null;
    const last = history[history.length - 1];
    const prev = history[history.length - 2];
    const delta = (last.credits || 0) - (prev.credits || 0);
    const pct = prev.credits > 0 ? (delta / prev.credits) * 100 : 0;
    return { delta, pct, up: delta > 0, down: delta < 0 };
  }, [history]);

  const livePreview = useMemo(() => {
    const c = Number(creditsInput);
    const r = Number(rateInput);
    if (!Number.isFinite(c) || !Number.isFinite(r)) return null;
    return c * r;
  }, [creditsInput, rateInput]);

  const openDialog = () => {
    setCreditsInput(totalCredits ? String(totalCredits) : "");
    setRateInput(ratePerCredit ? String(ratePerCredit) : "13.70");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const c = Number(creditsInput);
    const r = Number(rateInput);
    if (!Number.isFinite(c) || c < 0) {
      toast.error("Enter a valid total credits value");
      return;
    }
    if (!Number.isFinite(r) || r <= 0) {
      toast.error("Enter a valid per-credit rate");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/cost-tracker`,
        { total_credits: c, cost_per_credit_inr: r },
        { headers: authHeaders() }
      );
      // Refetch state + history so the chart reflects the new point.
      await fetchAll();
      toast.success("Cost tracker updated");
      setDialogOpen(false);
      setExpanded(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update cost tracker");
    } finally {
      setSaving(false);
    }
  };

  // Recharts data with parsed dates for axis formatting
  const chartData = useMemo(
    () => (history || []).map((p) => ({
      ...p,
      date: parseISO(p.week_start),
    })),
    [history]
  );

  const renderStaleDot = () => (
    <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-70" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
    </span>
  );

  const updatedByEmail = (state?.updated_by || "").split("@")[0] || "—";
  const lastUpdatedText = state?.updated_at
    ? `Last updated ${format(new Date(state.updated_at), "PPp")} by ${updatedByEmail}`
    : "Not updated yet";

  return (
    <div ref={containerRef} className="relative" data-testid="cost-tracker-widget">
      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="relative h-10 w-10 rounded-full flex items-center justify-center hover:bg-[#E8E8E8]/60 transition-colors"
          aria-label="Open cost tracker"
          title="App development cost tracker"
          data-testid="cost-tracker-icon-button"
        >
          <img
            src="/icons/cost-rupee.png"
            alt=""
            className="h-7 w-7 hover:scale-110 transition-transform"
          />
          {stale && renderStaleDot()}
        </button>
      ) : (
        <div
          className="group h-11 inline-flex items-stretch overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm animate-in fade-in slide-in-from-right-2 duration-200 cursor-pointer"
          onClick={openDialog}
          data-testid="cost-tracker-pill"
        >
          {/* Left cell: credits */}
          <div className="flex items-center gap-2 px-3 bg-slate-50/50">
            <img src="/icons/cost-rupee.png" alt="" className="h-4 w-4 opacity-80" />
            <div className="leading-tight">
              <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">CREDITS</div>
              <div className="text-[13px] font-bold tabular-nums text-slate-700" data-testid="cost-tracker-credits-display">
                {compactNumber(totalCredits)}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="w-px bg-slate-200" />

          {/* Right cell: spent */}
          <div className="flex items-center gap-2.5 px-3 pr-3">
            <div className="leading-tight">
              <div className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: TEAL }}>SPENT</div>
              <div className="text-[13px] font-bold tabular-nums text-slate-800" data-testid="cost-tracker-spent-display">
                {formatINR(totalSpent)}
              </div>
            </div>

            {sparklinePoints && (
              <svg width={sparklinePoints.w} height={sparklinePoints.h} viewBox={`0 0 ${sparklinePoints.w} ${sparklinePoints.h}`} className="shrink-0">
                <polyline
                  fill="none"
                  stroke={TEAL}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points={sparklinePoints.points.map(([x, y]) => `${x},${y}`).join(" ")}
                />
                {(() => {
                  const [ex, ey] = sparklinePoints.points[sparklinePoints.points.length - 1];
                  return <circle cx={ex} cy={ey} r="2" fill={TEAL} />;
                })()}
              </svg>
            )}

            {stale ? (
              <span className="relative h-2.5 w-2.5 inline-flex shrink-0" aria-label="Update needed">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-70" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
              </span>
            ) : (
              <Pencil className="h-3.5 w-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            )}
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="cost-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <img src="/icons/cost-rupee.png" alt="" className="h-5 w-5" />
              <span>App Development Cost Tracker</span>
            </DialogTitle>
          </DialogHeader>

          {stale && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900 text-[12px]" data-testid="cost-tracker-stale-alert">
              <AlertCircle className="h-4 w-4 mt-px shrink-0" />
              <span>It&apos;s a new week — please update the credits consumed since last Monday.</span>
            </div>
          )}

          {/* Trend chart OR empty state */}
          {history.length >= 2 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-start justify-between gap-3 mb-1">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-700">WEEK-ON-WEEK CREDITS</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Last {history.length} week{history.length === 1 ? "" : "s"} · Mondays
                  </div>
                </div>
                {trend && (trend.up || trend.down) && (
                  <div
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      trend.up ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"
                    }`}
                    data-testid="cost-tracker-trend-chip"
                  >
                    {trend.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {trend.up ? "+" : ""}{compactNumber(trend.delta)} cr ({trend.pct >= 0 ? "+" : ""}{trend.pct.toFixed(0)}%)
                  </div>
                )}
              </div>
              <div className="h-[176px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="ctTeal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={TEAL} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={TEAL} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => {
                        try { return format(d, "d MMM"); } catch { return ""; }
                      }}
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      tickLine={false}
                      axisLine={{ stroke: "#E8E8E8" }}
                    />
                    <YAxis
                      tickFormatter={(v) => compactNumber(v)}
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: "1px solid #E8E8E8", fontSize: 12 }}
                      labelFormatter={(d) => {
                        try { return format(d, "PP"); } catch { return ""; }
                      }}
                      formatter={(v, name) => {
                        if (name === "credits") return [compactNumber(v), "Credits"];
                        return [v, name];
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="credits"
                      stroke={TEAL}
                      strokeWidth={2}
                      fill="url(#ctTeal)"
                      dot={{ r: 3, fill: "#fff", stroke: TEAL, strokeWidth: 2 }}
                      activeDot={{ r: 4 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/40 px-4 py-6 text-center text-[12px] text-slate-500" data-testid="cost-tracker-empty-state">
              Save updates each week to build the trendline.
            </div>
          )}

          {/* Inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ct-credits" className="text-[12px]">Total credits consumed</Label>
              <Input
                id="ct-credits"
                type="number"
                step="any"
                min="0"
                placeholder="0"
                value={creditsInput}
                onChange={(e) => setCreditsInput(e.target.value)}
                data-testid="cost-tracker-credits-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ct-rate" className="text-[12px]">Cost per credit (₹)</Label>
              <Input
                id="ct-rate"
                type="number"
                step="any"
                min="0"
                placeholder="13.70"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                data-testid="cost-tracker-rate-input"
              />
              <p className="text-[10px] text-slate-400">Default ₹13.70 — edit anytime as billing changes.</p>
            </div>
          </div>

          {/* Live total preview */}
          <div className="rounded-lg bg-slate-50/60 border border-slate-200 px-4 py-3 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Estimated total</span>
            <span className="text-xl font-bold tabular-nums text-slate-800" data-testid="cost-tracker-live-total">
              {livePreview != null ? formatINR(livePreview) : formatINR(totalSpent)}
            </span>
          </div>

          <p className="text-[11px] text-slate-400" data-testid="cost-tracker-last-updated">
            {lastUpdatedText}
          </p>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} data-testid="cost-tracker-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="text-white"
              style={{ backgroundColor: TEAL }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = TEAL_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = TEAL)}
              data-testid="cost-tracker-save-btn"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
